import React, { useEffect, memo, useState, useCallback } from 'react';
import { AppToolbox } from '../app/appToolbox';
import { AppRow } from '../../layout/appRow';
import { AppControlledCheckbox } from '../app/input/appControlledCheckbox';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { Box } from '@mui/material';
import { AppControlledTextField } from '../app/input/appControlledTextField';
import { useDebounceFn } from '../../hooks/useDebounce';
import { AppControlledAutocomplete } from '../app/input/appControlledAutocomplete';
import { AutocompleteOption } from '../../models/autocompleteOption';
import { ROLES_ID } from '../../models/employee';
import { getOptions, getOptionsNameOnly } from '../../services/employeeService';
import { contractTypeOptions, employeeRoleOptions } from '../../constants';

import {
  GetDepartments,
  getDepartments,
  getEmployees,
  GetEmployeesParams,
} from '../../services/api/employeeServices';
import { NO_RESULTS, PLEASE_TYPE } from '../../constants';

interface Props {
  handleSubmit: (data: FieldValues) => void;
}

const paramsTeamLead: GetEmployeesParams = {
  includeDeleted: true,
  includeInactive: true,
  roleId: ROLES_ID.TEAM_LEAD,
  take: 10,
};

const paramsDepartments: GetDepartments = {
  take: 10,
};

const style = {
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    pb: 2,
    gap: 2,
  },
  textFieldsBox: { display: 'flex', flexWrap: 'wrap', gap: 2, width: '100%' },
  textField: { flex: 1, minWidth: '180px' },

  checkboxLabel: {
    mr: 0,
    minWidth: '180px',
  },
};

function ToolboxEmployeesBase({ handleSubmit }: Props): JSX.Element {
  const debounce = useDebounceFn(250);

  const [teamLeadsOptions, setTeamLeadsOptions] = useState<
    AutocompleteOption[]
  >([]);

  const [departmentsOptions, setDepartmentsOptions] = useState<
    AutocompleteOption[]
  >([]);

  const [isLoadingDepartments, setIsLoadingDepartments] =
    useState<boolean>(false);
  const [isLoadingTeamLeads, setIsLoadingTeamLeads] = useState<boolean>(false);

  const methods = useForm();
  const { watch } = methods;

  useEffect(() => {
    const fields = ['filter', 'isActive', 'department'];

    const subscription = watch((value, { name }) => {
      if (fields.includes(name || '')) {
        debounce(() => {
          handleSubmit(value);
        });
      } else {
        handleSubmit(value);
      }
    });

    return () => subscription.unsubscribe();
  }, [debounce, handleSubmit, watch]);

  const searchTeamLeads = useCallback((name: string) => {
    setIsLoadingTeamLeads(true);
    getEmployees({ ...paramsTeamLead, filter: name })
      .then((resp) => {
        setTeamLeadsOptions(getOptions(resp.items));
      })
      .finally(() => setIsLoadingTeamLeads(false));
  }, []);

  const searchDepartments = useCallback((name: string) => {
    setIsLoadingDepartments(true);
    getDepartments({ ...paramsDepartments, filter: name })
      .then((resp) => {
        setDepartmentsOptions(getOptionsNameOnly(resp.items));
      })
      .finally(() => setIsLoadingDepartments(false));
  }, []);

  useEffect(() => {
    searchTeamLeads('');
    searchDepartments('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AppToolbox
      pageTitle="Employees"
      thirdChildren={
        <FormProvider {...methods}>
          <AppRow sx={style.container}>
            <AppControlledTextField
              name="filter"
              label="Employee"
              sx={style.textField}
              defaultValue=""
              placeholder={PLEASE_TYPE}
            />
            <AppControlledAutocomplete
              name="department"
              label="Department"
              options={departmentsOptions}
              searchOptions={searchDepartments}
              sx={style.textField}
              isLoading={isLoadingDepartments}
              noOptionsText={NO_RESULTS}
              placeholder={PLEASE_TYPE}
            />

            <AppControlledAutocomplete
              name="teamLeadGuid"
              label="Team Lead"
              options={teamLeadsOptions}
              searchOptions={searchTeamLeads}
              sx={style.textField}
              isLoading={isLoadingTeamLeads}
              noOptionsText={NO_RESULTS}
              placeholder={PLEASE_TYPE}
            />

            <AppControlledAutocomplete
              name="employeeType"
              label="Contract Type"
              options={contractTypeOptions}
              sx={style.textField}
            />

            <AppControlledAutocomplete
              name="roleId"
              label="Role"
              options={employeeRoleOptions}
              sx={style.textField}
            />

            <Box>
              <AppControlledCheckbox
                name="isActive"
                label="Show also inactive"
                labelSx={style.checkboxLabel}
              />
            </Box>
          </AppRow>
        </FormProvider>
      }
    />
  );
}

export const ToolboxEmployees = memo(ToolboxEmployeesBase);
